import { Navigate, useNavigate } from 'react-router-dom'
import { getAuthToken } from '../../../services/authHeaders'
import { forgetPreviousUser } from '../../../services/signInState'
import { ChangePasswordForm } from './ChangePasswordForm'
import { changePasswordRequest } from './changePasswordRequest'
import { storeReissuedToken } from './reissuedSession'
import { coordinatorHomePath, isPasswordChangeRequired, recordPasswordChangeRequired } from './passwordChangeRequired'

/**
 * The one screen a coordinator with a temporary password can use. There is no way past it: every other
 * coordinator route redirects here while the flag is set, and the server refuses every other request anyway.
 * On success the server re-issues the session (#45); it is stored, the flag is cleared, and the user goes on.
 */
export default function ForcedPasswordChangePage({ apiBaseUrl }: { apiBaseUrl: string }) {
  const navigate = useNavigate()
  const isAuthenticated = (() => {
    try { return Boolean(localStorage.getItem('isAuthenticated')) } catch { return false }
  })()
  const isSuperadmin = (() => {
    try { return localStorage.getItem('isSuperadmin') === 'true' } catch { return false }
  })()

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isPasswordChangeRequired()) return <Navigate to={coordinatorHomePath(isSuperadmin)} replace />

  const changePassword = async (current: string, next: string) => {
    const previous = getAuthToken()
    const result = await changePasswordRequest(apiBaseUrl, previous, current, next)
    if (result.success && result.token) storeReissuedToken(previous, result.token)
    return result
  }

  // The only other way out of this screen. forgetPreviousUser clears every user-scoped key, the flag included.
  const signOut = () => {
    forgetPreviousUser()
    navigate('/login', { replace: true })
  }

  const onChanged = () => {
    recordPasswordChangeRequired(false)
    navigate(coordinatorHomePath(isSuperadmin), { replace: true })
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem',
      background: 'var(--ra-navy, #0a192f)' }}>
      <main style={{ maxWidth: 480, width: '100%', padding: '2rem', borderRadius: 12,
        border: '1px solid var(--ra-glass-border, rgba(255,255,255,0.18))', background: 'rgba(255,255,255,0.03)' }}>
        <h1 style={{ fontSize: '1.4rem', marginTop: 0 }}>Choose your own password</h1>
        <p style={{ color: 'var(--ra-text-secondary, #a8b2cc)', fontSize: '0.95rem', lineHeight: 1.5 }}>
          You signed in with a temporary password from your administrator. Replace it with one only you know
          before continuing. Enter the temporary password as your current password.
        </p>
        <ChangePasswordForm changePassword={changePassword} onChanged={onChanged} />
        <button type="button" onClick={signOut} style={{
          marginTop: '1.25rem', padding: 0, border: 'none', background: 'none', cursor: 'pointer',
          color: 'var(--ra-text-secondary, #a8b2cc)', fontSize: '0.9rem', textDecoration: 'underline',
        }}>
          Sign out instead
        </button>
      </main>
    </div>
  )
}
